





































































































































import Vue from 'vue';
import draggable from 'vuedraggable';
import { ICampaignBlueprint, ICommonABTest } from '@/store/types';
import { mapGetters } from 'vuex';
import * as actions from '@/store/action-types';
import * as mutations from '@/store/mutation-types';
import CampaignBlueprintCard from '@/components/Reactivation/CampaignBlueprintCard.vue';
import ABTestInfo from '@/components/Reactivation/ABTest/ABTestInfo.vue';
import ABTestCreateModal from '@/components/Reactivation/ABTest/ABTestCreateModal.vue';
import CardLoader from '@/components/UI/Loader/CardLoader.vue';
import { descending } from 'd3-array';

export default Vue.extend({
  name: 'ReactivationCampaigns',
  components: {
    draggable,
    CampaignBlueprintCard,
    CardLoader,
    ABTestInfo,
    ABTestCreateModal,
  },
  data() {
    return {
      drag: false as boolean,
      publishLoading: {} as { [key: string]: boolean },
      abTest: {
        name: '',
        winner: '',
      } as {
        [key: string]: string | ICommonABTest | undefined;
        name: string;
        winner: string;
        selectedABTest: ICommonABTest | undefined;
      },
      showABTestConfirm: false as boolean,
      loading: true,
      creatingCampaign: false,
      orderLocked: false,
    };
  },
  async created() {
    await this.$store.dispatch(actions.SYNC_REACTIVATION);
    this.loading = false;
  },
  computed: {
    ...mapGetters([
      'primaryReactivationCampaignBlueprintId',
      'reactivationCampaignBlueprints',
      'senderDomainReady',
      'isReadyToActivate',
      'primaryReactivationCampaignBlueprint',
    ]),
    dragOptions() {
      return {
        animation: 200,
        group: 'description',
        disabled: false,
        ghostClass: 'ghost',
      };
    },
    segmentedCampaignBlueprints(): ICampaignBlueprint[] {
      return this.reactivationCampaignBlueprints?.filter(({ audience, deleted }: ICampaignBlueprint) => audience && !deleted) || [];
    },
    enabledCampaignBlueprints(): ICampaignBlueprint[] {
      return this.segmentedCampaignBlueprints.filter(({ enabled }) => enabled);
    },
    disabledCampaignBlueprints(): ICampaignBlueprint[] {
      return this.segmentedCampaignBlueprints.filter(({ enabled }) => !enabled);
    },
    primaryPublished(): boolean {
      return this.primaryReactivationCampaignBlueprint?.publishedAt || false;
    },
  },
  methods: {
    async createCampaignBlueprint() {
      this.loading = true;
      this.creatingCampaign = true;

      const campaignBlueprint = await this.$store.dispatch(actions.CREATE_REACTIVATION_CAMPAIGN_BLUEPRINT, { segmented: true });
      await this.$store.dispatch(actions.SYNC_REACTIVATION);
      this.handleEdit(campaignBlueprint._id);
    },
    handleEdit(campaignBlueprintId: string) {
      if (campaignBlueprintId) {
        this.$router.push({ name: 'reactivationcampaign', params: { id: campaignBlueprintId } });
      }
    },
    updateABTest(campaignBlueprint: ICampaignBlueprint, payload: Partial<ICommonABTest>) {
      this.drag = true;
      if (typeof campaignBlueprint.abTests?.[0] !== 'undefined') {
        campaignBlueprint.abTests[0] = {
          ...campaignBlueprint.abTests[0],
          ...payload,
        };
      }
      this.drag = false;
    },
    async onDragEnd(order: { newIndex: number; oldIndex: number }) {
      if (order.newIndex !== order.oldIndex) {
        const campaignBlueprints = this.enabledCampaignBlueprints.map(({ _id }) => _id);
        this.orderLocked = true;
        await this.$store.dispatch(actions.UPDATE_REACTIVATION_CAMPAIGN_BLUEPRINTS_ORDER, { campaignBlueprints });
        this.orderLocked = false;
      }
      this.drag = false;
    },
    handleDelete(campaignBlueprintId: string) {
      if (campaignBlueprintId) {
        this.$store.commit(mutations.STORE_REACTIVATION_CAMPAIGN_BLUEPRINT_DATA, { id: campaignBlueprintId, data: { deleted: true } });
        this.$store.dispatch(actions.SCHEDULE_SAVE_REACTIVATION_CAMPAIGN_BLUEPRINT, { id: campaignBlueprintId, delay: 0 });
      }
    },
    async handlePublish(campaignBlueprintId: string, silent = false) {
      try {
        if (campaignBlueprintId) {
          this.orderLocked = true;
          this.publishLoading[campaignBlueprintId] = true;
          await this.$store.dispatch(actions.PUBLISH_REACTIVATION_CAMPAIGN_BLUEPRINT, { id: campaignBlueprintId });
          this.publishLoading[campaignBlueprintId] = false;
          if (!silent) {
            this.$message.success("It's live! Reactivation campaign successfully published.");
          }
          this.orderLocked = false;
        }
      } catch (e) {
        this.$message.error('Failed to publish reactivation campaign. Please refresh and try again or contact us through chat');
        this.publishLoading[campaignBlueprintId] = false;
        console.log(e);
      }
    },
    async handleDisable(campaignBlueprintId: string) {
      if (campaignBlueprintId) {
        this.$store.commit(mutations.STORE_REACTIVATION_CAMPAIGN_BLUEPRINT_DATA, { id: campaignBlueprintId, data: { enabled: false } });
        await this.$store.dispatch(actions.SCHEDULE_SAVE_REACTIVATION_CAMPAIGN_BLUEPRINT, { id: campaignBlueprintId, delay: 0 });
        this.handlePublish(campaignBlueprintId, true);
      }
    },
    async handleEnable(campaignBlueprintId: string) {
      console.log('handleEnable', campaignBlueprintId);
      if (campaignBlueprintId) {
        this.$store.commit(mutations.STORE_REACTIVATION_CAMPAIGN_BLUEPRINT_DATA, { id: campaignBlueprintId, data: { enabled: true } });
        await this.$store.dispatch(actions.SCHEDULE_SAVE_REACTIVATION_CAMPAIGN_BLUEPRINT, { id: campaignBlueprintId, delay: 0 });
        this.handlePublish(campaignBlueprintId);
      }
    },
    handleClone(campaignBlueprintId: string) {
      if (campaignBlueprintId) {
        this.$store.dispatch(actions.CLONE_REACTIVATION_CAMPAIGN_BLUEPRINT, { id: campaignBlueprintId, delay: 0 });
      }
    },
    handleABTest(campaignBlueprintId: string) {
      if (campaignBlueprintId) {
        this.abTest.name = '';
        this.abTest.variantA = campaignBlueprintId;
        this.showABTestConfirm = true;
      }
    },
    getABTest(campaignBlueprint: ICampaignBlueprint) {
      return campaignBlueprint.abTests?.find(({ completedAt }: ICommonABTest) => !completedAt);
    },
    campaignBlueprintWithVariants(campaignBlueprint: ICampaignBlueprint) {
      const variants = this.getABTest(campaignBlueprint)?.variants || [];
      return [campaignBlueprint, ...variants];
    },
    closeCreateABTestDialog() {
      this.abTest.name = '';
      this.abTest.variantA = '';
      this.showABTestConfirm = false;
    },
  },
});
